import React, { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import { useTheme } from './ThemeContext';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const { primaryColor } = useTheme();
  const [toasts, setToasts] = useState([]);
  const recentMessages = useRef(new Map());

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message, variant = 'success', duration = null, title = null) => {
      // Deduplicate: skip if same message+variant was shown within 1 second
      const dedupeKey = `${variant}:${message}`;
      const now = Date.now();
      const lastShown = recentMessages.current.get(dedupeKey);
      if (lastShown && now - lastShown < 1000) {
        return null;
      }
      recentMessages.current.set(dedupeKey, now);

      const id = now + Math.random();
      const toast = {
        id,
        message,
        variant,
        title,
        timestamp: new Date().toLocaleString(),
      };

      setToasts((prev) => [...prev, toast]);

      // Auto-hide after specified duration or default
      const hideAfter = duration || (variant === 'danger' ? 5000 : 3000);
      setTimeout(() => {
        removeToast(id);
        recentMessages.current.delete(dedupeKey);
      }, hideAfter);

      return id;
    },
    [removeToast]
  );

  const showSuccess = useCallback(
    (message, duration, title) => showToast(message, 'success', duration, title),
    [showToast]
  );
  const showError = useCallback(
    (message, duration, title) => showToast(message, 'danger', duration, title),
    [showToast]
  );
  const showWarning = useCallback(
    (message, duration, title) => showToast(message, 'warning', duration, title),
    [showToast]
  );
  const showInfo = useCallback((message, duration, title) => showToast(message, 'info', duration, title), [showToast]);

  // Memoize context value to prevent cascading re-renders
  const contextValue = useMemo(
    () => ({
      showToast,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      removeToast,
    }),
    [showToast, showSuccess, showError, showWarning, showInfo, removeToast]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {/* Global Toast Container */}
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
        }}
      >
        {toasts.map((toast) => {
          // Variant-specific styles to match the provided screenshot
          const stylesByVariant = {
            success: {
              headerBg: '#e9f7ef', // light green
              headerText: '#0f5132', // dark green text
              bodyBg: '#198754', // bootstrap success
              bodyText: '#ffffff',
              border: '#badbcc',
            },
            danger: {
              headerBg: '#f8d7da',
              headerText: '#842029',
              bodyBg: '#dc3545',
              bodyText: '#ffffff',
              border: '#f5c2c7',
            },
            warning: {
              headerBg: '#fff3cd',
              headerText: '#664d03',
              bodyBg: '#ffc107',
              bodyText: '#212529',
              border: '#ffec99',
            },
            info: {
              headerBg: '#cff4fc',
              headerText: '#055160',
              bodyBg: '#0dcaf0',
              bodyText: '#052c65',
              border: '#b6effb',
            },
            default: {
              headerBg: `${primaryColor}22`,
              headerText: primaryColor,
              bodyBg: primaryColor,
              bodyText: '#ffffff',
              border: 'rgba(255,255,255,0.2)',
            },
          };
          const v = stylesByVariant[toast.variant] || stylesByVariant.default;
          return (
            <Toast
              key={toast.id}
              show={true}
              onClose={() => removeToast(toast.id)}
              className="mb-2"
              style={{
                backgroundColor: `${v.bodyBg}e6`,
                backdropFilter: 'blur(16px) saturate(180%)',
                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                color: v.bodyText,
                border: `1px solid ${v.border}`,
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                overflow: 'hidden',
                animation: 'glass-card-enter 0.3s ease-out',
              }}
            >
              <Toast.Header
                style={{
                  backgroundColor: `${v.headerBg}cc`,
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: v.headerText,
                  borderBottom: 'none',
                  borderRadius: '16px 16px 0 0',
                }}
              >
                <strong className="me-auto" style={{ color: v.headerText }}>
                  <i
                    className={`bi ${
                      toast.variant === 'success'
                        ? 'bi-check-circle'
                        : toast.variant === 'danger'
                          ? 'bi-exclamation-triangle'
                          : toast.variant === 'warning'
                            ? 'bi-exclamation-triangle'
                            : toast.variant === 'info'
                              ? 'bi-info-circle'
                              : 'bi-bell'
                    } me-2`}
                  ></i>
                  {toast.title ||
                    (toast.variant === 'success'
                      ? 'Success'
                      : toast.variant === 'danger'
                        ? 'Error'
                        : toast.variant === 'warning'
                          ? 'Warning'
                          : toast.variant === 'info'
                            ? 'Info'
                            : 'Notification')}
                </strong>
                <small style={{ color: v.headerText }}>{toast.timestamp}</small>
              </Toast.Header>
              <Toast.Body style={{ color: v.bodyText }}>{toast.message}</Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>
    </ToastContext.Provider>
  );
};
